// @flow
import { cloneDeep, difference, union, uniqBy } from 'lodash';
import { getAuthToken, getBidderId } from '../modules/user';
import api from '../api/conversationMessages';
import { createSelector } from 'reselect';
import { getDeployment } from '../modules/config';
import type { GlobalState } from '../rootReducer';

/* Action Types */
export const LOAD_CONVERSATION_MESSAGES_FAIL = 'la/domain/loadMessages/LOAD_FAIL';
export const LOAD_CONVERSATION_MESSAGES_REQUEST = 'la/domain/loadMessages/LOAD_REQUEST';
export const LOAD_CONVERSATION_MESSAGES_SUCCESS = 'la/domain/loadMessages/LOAD_SUCCESS';

export const SEND_CONVERSATION_MESSAGE_FAIL = 'la/domain/sendMessage/LOAD_FAIL';
export const SEND_CONVERSATION_MESSAGE_REQUEST = 'la/domain/sendMessage/LOAD_REQUEST';
export const SEND_CONVERSATION_MESSAGE_SUCCESS = 'la/domain/sendMessage/LOAD_SUCCESS';

/* reducer */
const DEFAULT_STATE = {
    byId: {},
    loaded: {},
    loading: [],
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;
    let loaded;
    let loading;
    let time;

    switch (action.type) {
        case SEND_CONVERSATION_MESSAGE_FAIL:
            return {
                ...state,
                loading: difference(state.loading, [action.meta.conversationId]),
            };
        case SEND_CONVERSATION_MESSAGE_REQUEST:
            return {
                ...state,
                loading: union(state.loading, action.payload),
            };
        case SEND_CONVERSATION_MESSAGE_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = cloneDeep(state.loaded);
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;

            if (Array.isArray(action.payload.payload)) {
                const messages = action.payload.payload;
                existing[action.meta.conversationId] = uniqBy(
                    [...existing[action.meta.conversationId], ...messages],
                    'messageId'
                );
                loaded[action.meta.conversationId] = time;
                loading = difference(loading, [action.meta.conversationId]);
            }

            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        case LOAD_CONVERSATION_MESSAGES_FAIL:
            return {
                ...state,
                loading: difference(state.loading, [action.meta.conversationId]),
            };
        case LOAD_CONVERSATION_MESSAGES_REQUEST:
            return {
                ...state,
                loading: union(state.loading, action.payload),
            };
        case LOAD_CONVERSATION_MESSAGES_SUCCESS:
            existing = cloneDeep(state.byId);
            loaded = cloneDeep(state.loaded);
            loading = cloneDeep(state.loading);
            time = action.meta.actionTime;

            if (Array.isArray(action.payload)) {
                existing[action.meta.conversationId] = action.payload;
                loaded[action.meta.conversationId] = time;
                loading = difference(loading, [action.meta.conversationId]);
            }
            return {
                ...state,
                byId: existing,
                loaded,
                loading,
            };
        default:
            return state;
    }
}

const stateSelector = (state: GlobalState) => state.conversationMessages;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, state => state.byId);

const loadedSelector = createSelector(stateSelector, state => state.loaded);

const loadingSelector = createSelector(stateSelector, state => state.loading);

export const getConversationMessages = createSelector([byIdSelector, idSelector], (byId, id) => byId[id] || []);

export const getMessage = createSelector([byIdSelector, idSelector], (byId, id) => byId[id] || {});

export const getLoadTimeForConversationMessages = createSelector(
    [loadedSelector, idSelector],
    (loaded, id) => loaded[id] || 0
);

export const isConversationMessagesLoading = createSelector(loadingSelector, loading => loading);

const loadConversationMessages = (conversationId: number) => async (dispatch, getState) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const bidderId = getBidderId(state);
        dispatch({
            type: LOAD_CONVERSATION_MESSAGES_REQUEST,
        });
        const response = await api.fetchConversationMessages({ authToken, bidderId, conversationId, deployment });
        return dispatch({
            meta: { actionTime: Date.now(), conversationId },
            payload: response.payload,
            type: LOAD_CONVERSATION_MESSAGES_SUCCESS,
        });
    } catch (error) {
        return dispatch({
            error: true,
            meta: { conversationId },
            payload: error,
            type: LOAD_CONVERSATION_MESSAGES_FAIL,
        });
    }
};

const sendMessage = (conversationId: number, messageBody: any, attachedFiles: any[]) => async (dispatch, getState) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        const bidderId = getBidderId(state);
        dispatch({
            type: SEND_CONVERSATION_MESSAGE_REQUEST,
        });
        const response = await api.sendConversationMessage({
            attachedFiles,
            authToken,
            bidderId,
            conversationId,
            deployment,
            messageBody,
        });
        return dispatch({
            meta: { actionTime: Date.now(), conversationId },
            payload: response.data,
            type: SEND_CONVERSATION_MESSAGE_SUCCESS,
        });
    } catch (error) {
        return dispatch({
            error: true,
            meta: { conversationId },
            payload: error,
            type: SEND_CONVERSATION_MESSAGE_FAIL,
        });
    }
};

export const sendConversationMessage = (conversationId: number, messageBody: any, attachedFiles: any[]) => async (
    dispatch: Function,
    getState: Function
) => {
    return dispatch(sendMessage(conversationId, messageBody, attachedFiles));
};

export const fetchConversationMessages = (conversationId: number) => async (dispatch: Function, getState: Function) => {
    return dispatch(loadConversationMessages(conversationId));
};
